/**
 * formats.js
 * Every size a picture might need, as data.
 *
 * Kept as a plain catalogue rather than scattered through the screen that uses
 * it, because these numbers change: platforms adjust their crops, add a
 * placement, or retire one. When that happens the fix should be editing a row
 * here, not hunting through rendering code.
 *
 * Each entry says what it is *for*, not just its dimensions. "1080×1920" means
 * nothing on its own; "Story or Reel — full screen on a phone" is the thing
 * somebody is actually choosing.
 *
 * The ratios are the part that matters most. A platform will resize a picture
 * that is the wrong number of pixels, but it will crop or refuse one that is
 * the wrong shape — so these are grouped by shape and the note says what gets
 * lost when the shape is wrong.
 */

export const OUTPUT_FORMATS = {
  'image/jpeg': {
    label: 'JPEG',
    extension: 'jpg',
    lossy: true,
    note: 'Smaller files, no transparency. What Instagram requires.',
  },
  'image/png': {
    label: 'PNG',
    extension: 'png',
    lossy: false,
    note: 'Keeps transparency and sharp edges. Best for logos and screenshots.',
  },
  'image/webp': {
    label: 'WebP',
    extension: 'webp',
    lossy: true,
    note: 'Smallest of the three, with transparency. Not accepted everywhere.',
  },
};

/**
 * The catalogue.
 *
 * `ratio` is derived rather than written out, so a corrected width or height
 * cannot leave a stale ratio behind it.
 */
const RAW = [
  // ---------- Instagram ----------
  { platform: 'instagram', key: 'ig-square', label: 'Square post', width: 1080, height: 1080, note: 'Safe everywhere. The least likely to be cropped.' },
  { platform: 'instagram', key: 'ig-portrait', label: 'Portrait post', width: 1080, height: 1350, note: 'Takes the most room in the feed. Usually the best performer.' },
  { platform: 'instagram', key: 'ig-landscape', label: 'Landscape post', width: 1080, height: 566, note: 'The widest Instagram allows in a feed post.' },
  { platform: 'instagram', key: 'ig-story', label: 'Story or Reel', width: 1080, height: 1920, note: 'Full screen on a phone. Keep text away from the top and bottom fifth.' },
  { platform: 'instagram', key: 'ig-profile', label: 'Profile picture', width: 320, height: 320, note: 'Shown as a circle, so anything in the corners is lost.' },

  // ---------- Facebook ----------
  { platform: 'facebook', key: 'fb-square', label: 'Square post', width: 1080, height: 1080, note: 'Safe everywhere.' },
  { platform: 'facebook', key: 'fb-portrait', label: 'Portrait post', width: 1080, height: 1350, note: 'Taller in the feed on a phone.' },
  { platform: 'facebook', key: 'fb-link', label: 'Link preview', width: 1200, height: 630, note: 'The image on a shared link. Anything else gets cropped to this.' },
  { platform: 'facebook', key: 'fb-story', label: 'Story or Reel', width: 1080, height: 1920, note: 'Full screen on a phone.' },
  { platform: 'facebook', key: 'fb-cover', label: 'Page cover', width: 1640, height: 856, note: 'Cropped differently on desktop and phone — keep anything important centred.' },
  { platform: 'facebook', key: 'fb-event', label: 'Event cover', width: 1920, height: 1005, note: 'Wide banner at the top of an event.' },
  { platform: 'facebook', key: 'fb-profile', label: 'Profile picture', width: 320, height: 320, note: 'Shown as a circle.' },

  // ---------- LinkedIn ----------
  { platform: 'linkedin', key: 'li-square', label: 'Square post', width: 1200, height: 1200, note: 'Safe everywhere.' },
  { platform: 'linkedin', key: 'li-portrait', label: 'Portrait post', width: 1080, height: 1350, note: 'Taller in the feed on a phone.' },
  { platform: 'linkedin', key: 'li-landscape', label: 'Landscape post', width: 1200, height: 627, note: 'The standard wide post image.' },
  { platform: 'linkedin', key: 'li-article', label: 'Article or link', width: 1200, height: 627, note: 'The image on a shared article.' },
  { platform: 'linkedin', key: 'li-company-cover', label: 'Company cover', width: 1128, height: 191, note: 'Very wide and short. Text rarely survives it.' },
  { platform: 'linkedin', key: 'li-personal-banner', label: 'Personal banner', width: 1584, height: 396, note: 'The banner on a personal profile.' },
  { platform: 'linkedin', key: 'li-profile', label: 'Profile picture', width: 400, height: 400, note: 'Shown as a circle.' },

  // ---------- TikTok ----------
  { platform: 'tiktok', key: 'tt-video', label: 'Video or photo post', width: 1080, height: 1920, note: 'Full screen. The interface covers the right edge and the bottom third.' },
  { platform: 'tiktok', key: 'tt-cover', label: 'Video cover', width: 1080, height: 1920, note: 'The still shown before a video plays.' },
  { platform: 'tiktok', key: 'tt-profile', label: 'Profile picture', width: 200, height: 200, note: 'Shown as a circle.' },
];

export const PLATFORMS = {
  instagram: { label: 'Instagram', order: 1 },
  facebook: { label: 'Facebook', order: 2 },
  linkedin: { label: 'LinkedIn', order: 3 },
  tiktok: { label: 'TikTok', order: 4 },
};

/** Every preset, with its ratio worked out rather than written down twice. */
export const PRESETS = RAW.map((preset) => ({
  ...preset,
  ratio: preset.width / preset.height,
  shape: describeShape(preset.width / preset.height),
  platformLabel: PLATFORMS[preset.platform]?.label ?? preset.platform,
}));

export const byKey = (key) => PRESETS.find((p) => p.key === key) ?? null;

export const forPlatform = (platform) => PRESETS.filter((p) => p.platform === platform);

/** "9:16", "4:5", "1:1" — the ratio as people say it. */
export function describeShape(ratio) {
  const known = [
    [1, '1:1'],
    [4 / 5, '4:5'],
    [1.91, '1.91:1'],
    [9 / 16, '9:16'],
    [16 / 9, '16:9'],
    [1200 / 627, '1.91:1'],
    [1128 / 191, 'ultra-wide'],
    [1584 / 396, '4:1'],
  ];

  for (const [value, label] of known) {
    if (Math.abs(ratio - value) < 0.02) return label;
  }
  return `${ratio.toFixed(2)}:1`;
}

/**
 * Presets that share a shape with the one given.
 *
 * Useful because a single render often covers several: an Instagram Story, a
 * Facebook Story and a TikTok post are all 1080×1920, and offering three
 * separate downloads of an identical file would be silly.
 */
export function sameShape(preset) {
  return PRESETS.filter(
    (other) => other.key !== preset.key && Math.abs(other.ratio - preset.ratio) < 0.01,
  );
}

/**
 * One preset per distinct size, so a batch does not render the same pixels
 * several times over.
 *
 * Returns each unique size with the list of presets it satisfies, which is
 * also what the screen should say: "1080×1920 — covers Instagram Story,
 * Facebook Story and TikTok".
 */
export function dedupe(presets) {
  const groups = new Map();

  for (const preset of presets) {
    const key = `${preset.width}x${preset.height}`;
    if (!groups.has(key)) {
      groups.set(key, { width: preset.width, height: preset.height, ratio: preset.ratio, covers: [] });
    }
    groups.get(key).covers.push(preset);
  }

  return [...groups.values()];
}

/** A filename that says what it is, months later in a downloads folder. */
export function outputName(sourceName, preset, mime) {
  const base = String(sourceName ?? 'image').replace(/\.[^.]+$/, '') || 'image';
  const extension = OUTPUT_FORMATS[mime]?.extension ?? 'jpg';
  return `${base}-${preset.width}x${preset.height}.${extension}`;
}

// ============================================
// SIZES OF YOUR OWN
// ============================================

/**
 * Sizes somebody types in themselves.
 *
 * The catalogue above is what the platforms want; this is everything else — a
 * website banner, an email header, a print, a size a client asked for in an
 * email. They are stored as the same shape as a preset so nothing downstream
 * has to know the difference.
 *
 * The bounds are not arbitrary. Below sixteen pixels there is nothing to look
 * at, and the pixel total is the limit that actually bites: Safari on a phone
 * refuses a canvas over about sixteen million pixels, and the failure there is
 * a blank image rather than an error, which is far worse than being told no.
 */
export const CUSTOM_BOUNDS = { min: 16, max: 8000, maxPixels: 16_000_000 };

const CUSTOM_KEY = 'kreeboom.custom-sizes';

/**
 * Whether a typed width and height can be added, and what to say if not.
 *
 * Called on every keystroke, so it answers about an empty form without
 * complaining: `ok` is false and `error` is null until there is something to
 * be wrong about.
 *
 * @returns {{ok: boolean, error: string|null, note: string|null}}
 */
export function checkCustomSize(width, height, existing = []) {
  const w = Number(width);
  const h = Number(height);
  const typed = String(width ?? '').trim() !== '' && String(height ?? '').trim() !== '';

  if (!typed) return { ok: false, error: null, note: null };

  const no = (error) => ({ ok: false, error, note: null });

  if (!Number.isFinite(w) || !Number.isFinite(h) || w !== Math.round(w) || h !== Math.round(h)) {
    return no('Width and height have to be whole numbers of pixels.');
  }
  if (w < CUSTOM_BOUNDS.min || h < CUSTOM_BOUNDS.min) {
    return no(`${CUSTOM_BOUNDS.min} pixels is the smallest side this makes.`);
  }
  if (w > CUSTOM_BOUNDS.max || h > CUSTOM_BOUNDS.max) {
    return no(`${CUSTOM_BOUNDS.max} pixels is the longest side this makes.`);
  }
  if (w * h > CUSTOM_BOUNDS.maxPixels) {
    return no(
      `${w}×${h} is ${(w * h / 1_000_000).toFixed(1)} million pixels. Some browsers, phones ` +
        'especially, quietly produce a blank image above about 16 million — so this stops short of it.',
    );
  }
  if (existing.some((c) => c.width === w && c.height === h)) {
    return no(`You already have ${w}×${h} in your list.`);
  }

  // Not a refusal: making the same pixels twice is harmless, they are merged
  // into one file anyway, and knowing it has a name is more useful than a no.
  const match = PRESETS.find((p) => p.width === w && p.height === h);
  return {
    ok: true,
    error: null,
    note: match
      ? `That is the same as ${match.platformLabel} ${match.label.toLowerCase()} — ticking it above does the same job.`
      : null,
  };
}

/** A typed size, as a stored entry. */
export function makeCustomSize({ width, height, label }) {
  const w = Math.round(Number(width));
  const h = Math.round(Number(height));
  return {
    id: `${w}x${h}-${Date.now().toString(36)}`,
    label: String(label ?? '').trim().slice(0, 40),
    width: w,
    height: h,
    on: true,
  };
}

/**
 * A stored size, as something `dedupe` and `renderTo` can use.
 *
 * `platform: 'custom'` keeps it out of the platform groups on the screen,
 * which iterate over PLATFORMS rather than over whatever turns up.
 */
export function customToPreset(custom) {
  const ratio = custom.width / custom.height;
  return {
    platform: 'custom',
    platformLabel: 'Your own',
    key: `custom-${custom.id}`,
    label: custom.label || `${custom.width}×${custom.height}`,
    width: custom.width,
    height: custom.height,
    note: 'A size you added yourself.',
    ratio,
    shape: describeShape(ratio),
  };
}

/**
 * The list as it was left, or an empty one.
 *
 * Wrapped in a try because localStorage throws outright in a locked-down
 * browser, and a saved size is not worth taking the screen down for.
 */
export function loadCustomSizes() {
  try {
    const raw = JSON.parse(localStorage.getItem(CUSTOM_KEY) ?? '[]');
    if (!Array.isArray(raw)) return [];
    // Anything hand-edited or left by an older version is dropped rather than
    // trusted — a bad width reaches a canvas, and that is not the place to
    // find out.
    return raw
      .filter((c) => c && Number.isFinite(c.width) && Number.isFinite(c.height))
      .filter(
        (c) =>
          c.width >= CUSTOM_BOUNDS.min &&
          c.height >= CUSTOM_BOUNDS.min &&
          c.width <= CUSTOM_BOUNDS.max &&
          c.height <= CUSTOM_BOUNDS.max,
      )
      .map((c, i) => ({
        id: String(c.id ?? `${c.width}x${c.height}-${i}`),
        label: String(c.label ?? '').slice(0, 40),
        width: Math.round(c.width),
        height: Math.round(c.height),
        on: c.on !== false,
      }));
  } catch {
    return [];
  }
}

export function saveCustomSizes(list) {
  try {
    localStorage.setItem(CUSTOM_KEY, JSON.stringify(list ?? []));
  } catch {
    /* a full or blocked store is not worth an error on screen */
  }
}
